// @ts-strict-ignore
import React from 'react';
import type { ColumnDef } from '@usealloy/component-library';
import {
  Icon,
  IconName,
  Tooltip,
} from '@usealloy/component-library';
import moment from 'moment';
import type { MatchData, WatchlistResponse } from './types';
import {
  MatchStrengthBadge,
  StatusBadge,
  aiRecommendationColumn,
  aiAssistedColumn,
  createAiRecommendationColumn,
} from './components';
import type { AiRecommendationCallbacks } from './components';

export { aiRecommendationColumn, aiAssistedColumn, createAiRecommendationColumn };
export type { AiRecommendationCallbacks };

const datumOrDashes = (getValue, callback) => {
  const value = getValue();
  if (value === null || value === undefined || value === '') return '-';
  return callback(value);
};

const getAiColumn = (callbacks?: AiRecommendationCallbacks) => (
  callbacks ? createAiRecommendationColumn(callbacks) : aiRecommendationColumn
);

export const resolveModalTableColumns: ColumnDef<MatchData>[] = [
  {
    accessorFn: ({ name }) => name,
    enableSorting: false,
    header: 'Name',
    id: 'name',
    maxSize: 160,
  },
  {
    accessorFn: ({ screening_type }) => screening_type,
    enableSorting: false,
    header: 'Screening Type',
    id: 'screeningType',
    maxSize: 160,
  },
  {
    accessorFn: ({ service }) => service,
    enableSorting: false,
    header: 'Service',
    id: 'service',
    maxSize: 160,
  },
  {
    accessorFn: ({ match_types }) => match_types.map(({ label }) => label).join(', '),
    cell: ({ getValue }) => datumOrDashes(
      getValue,
      value => value,
    ),
    enableSorting: false,
    header: 'Type',
    id: 'matchTypes',
    maxSize: 160,
  },
  {
    accessorFn: ({ match_fields }) => match_fields.join(', '),
    cell: ({ getValue }) => datumOrDashes(
      getValue,
      value => value,
    ),
    enableSorting: false,
    header: 'Match Fields',
    id: 'matchFields',
    maxSize: 160,
  },
  {
    accessorFn: ({ match_strength }) => match_strength,
    header: 'Strength',
    cell: ({ getValue, row: { original: { service } } }) => datumOrDashes(
      getValue,
      value => <MatchStrengthBadge service={service} strength={value as number} />,
    ),
    id: 'matchStrength',
    size: 120,
  },
  {
    accessorFn: ({ last_updated }) => last_updated,
    cell: ({ getValue }) => datumOrDashes(
      getValue,
      value => moment(value).format('MM-DD-yyyy'),
    ),
    header: 'Last Updated',
    id: 'lastUpdated',
    size: 140,
  },
];

export const reviewTableColumns: ColumnDef<MatchData>[] = [
  {
    accessorFn: ({ name }) => name,
    header: 'Name',
    id: 'name',
    size: 140,
  },
  {
    accessorFn: ({ screening_type }) => screening_type,
    header: 'Screening Type',
    id: 'screeningType',
    size: 160,
  },
  {
    accessorFn: ({ service }) => service,
    header: 'Service',
    id: 'service',
    size: 140,
  },
  {
    accessorFn: ({ match_types }) => match_types.map(({ label }) => label).join(', '),
    enableSorting: false,
    id: 'matchTypes',
    header: 'Types',
    size: 140,
  },
  {
    accessorFn: ({ match_fields }) => match_fields.join(', '),
    cell: ({ getValue }) => datumOrDashes(
      getValue,
      value => value,
    ),
    enableSorting: false,
    header: 'Match Fields',
    id: 'matchFields',
    size: 140,
  },
  {
    accessorFn: ({ match_strength }) => match_strength,
    header: 'Strength',
    cell: ({ getValue, row: { original: { service } } }) => datumOrDashes(
      getValue,
      value => <MatchStrengthBadge service={service} strength={value as number} />,
    ),
    id: 'matchStrength',
    size: 120,
  },
  {
    accessorFn: ({ last_updated }) => last_updated,
    cell: ({ getValue }) => datumOrDashes(
      getValue,
      value => moment(value).format('MM-DD-yyyy'),
    ),
    header: 'Last Updated',
    id: 'lastUpdated',
    size: 140,
  },
];

export const allTableColumns: ColumnDef<MatchData>[] = [
  ...reviewTableColumns,
  {
    accessorFn: ({ status }) => status,
    cell: ({ getValue }) => (<StatusBadge status={getValue() as string} />),
    enableSorting: false,
    header: 'Status',
    id: 'status',
    maxSize: 140,
    minSize: 85,
    size: 85,
  },
  {
    accessorFn: ({ resolution }) => resolution.note,
    enableSorting: false,
    header: 'Note',
    id: 'note',
    size: 140,
  },
  {
    accessorFn: ({ is_suppressed }) => is_suppressed,
    cell: ({ getValue }) => (
      getValue()
        ? (
          <Icon icon={IconName.Approved} size="large" />
        ) : (
          <div />
        )
    ),
    enableSorting: false,
    header: 'Suppressed',
    id: 'isSuppressed',
    maxSize: 140,
    minSize: 85,
    size: 85,
  },
];

export const formatCrimeSeverity = (value: [string, string][]) => {
  const severityToCrimes = new Map<string, string[]>();
  value.forEach(([crime, severity]) => {
    const normalizedSeverity = typeof severity === 'string' ? severity.trim().toLowerCase() : '';
    const normalizedCrime = typeof crime === 'string' ? crime.trim() : '';
    if (!normalizedSeverity || !normalizedCrime) return;

    const crimes = severityToCrimes.get(normalizedSeverity) ?? [];
    const alreadyIncluded = crimes.some(c => c.toLowerCase() === normalizedCrime.toLowerCase());
    if (!alreadyIncluded) crimes.push(normalizedCrime);
    severityToCrimes.set(normalizedSeverity, crimes);
  });

  const severityKeys = Array.from(severityToCrimes.keys())
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const toTitleCase = (s: string) => s.replace(/\b\w/g, c => c.toUpperCase());

  return {
    summary: severityKeys.map(toTitleCase).join(', '),
    tooltip: severityKeys
      .map(severity => `${toTitleCase(severity)}: ${(severityToCrimes.get(severity) ?? []).join(', ')}`)
      .join('\n'),
  };
};

export const crimeSeverityColumn: ColumnDef<MatchData> = {
  accessorFn: ({ crime_severity }) => crime_severity,
  cell: ({ getValue }) => {
    const value = getValue() as [string, string][] | undefined;
    if (!value || value.length === 0) return '-';
    const { summary, tooltip } = formatCrimeSeverity(value);

    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--cl-space-1)' }}>
        <span>{summary}</span>
        <Tooltip content={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>} side="top">
          <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
            <Icon icon={IconName.Info} size="small" />
          </span>
        </Tooltip>
      </span>
    );
  },
  enableSorting: false,
  header: 'Crime Severity',
  id: 'crimeSeverity',
  size: 140,
};

export const getResolveModalTableColumns = (
  watchlistAgentEnabled: boolean,
  watchlistSopEnabled: boolean = false,
  callbacks?: AiRecommendationCallbacks,
): ColumnDef<MatchData>[] => {
  if (!watchlistAgentEnabled) {
    return resolveModalTableColumns;
  }

  const columns = [
    ...resolveModalTableColumns,
    getAiColumn(callbacks),
  ];

  if (watchlistSopEnabled) {
    columns.push(crimeSeverityColumn);
  }

  return columns;
};

export const getReviewTableColumns = (
  watchlistAgentEnabled: boolean,
  watchlistSopEnabled: boolean = false,
  callbacks?: AiRecommendationCallbacks,
): ColumnDef<MatchData>[] => {
  if (!watchlistAgentEnabled) {
    return reviewTableColumns;
  }

  const columns = [
    ...reviewTableColumns,
    getAiColumn(callbacks),
  ];

  if (watchlistSopEnabled) {
    columns.push(crimeSeverityColumn);
  }

  return columns;
};

export const getAllTableColumns = (
  watchlistAgentEnabled: boolean,
  watchlistSopEnabled: boolean = false,
  callbacks?: AiRecommendationCallbacks,
): ColumnDef<MatchData>[] => {
  if (!watchlistAgentEnabled) {
    return allTableColumns;
  }

  const aiColumn = getAiColumn(callbacks);

  // Insert AI Recommendation column after Last Updated
  const lastUpdatedIndex = allTableColumns.findIndex(col => col.id === 'lastUpdated');
  const insertIndex = lastUpdatedIndex !== -1 ? lastUpdatedIndex + 1 : allTableColumns.length;

  const columns = [
    ...allTableColumns.slice(0, insertIndex),
    aiColumn,
    ...allTableColumns.slice(insertIndex),
    aiAssistedColumn,
  ];

  if (watchlistSopEnabled) {
    columns.push(crimeSeverityColumn);
  }

  return columns;
};

export const initialWatchlistData = { matches: [], service: '' } as WatchlistResponse;

export const resolutionDecisionReasonOptions = [
  { label: 'No Match', value: 'No Match' },
  { label: 'False Positive', value: 'False Positive' },
  { label: 'Potential Match', value: 'Potential Match' },
  { label: 'True Match', value: 'True Match' },
  { label: 'Unknown', value: 'Unknown' },
];
